import { useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  Plus, RefreshCw, Power, Settings as SettingsIcon, TestTube, Trash2,
  Upload, Download, Search, X, Clock, Video, Eye,
  ChevronUp, ChevronDown, ChevronsUpDown,
} from "lucide-react";

import { Button } from "../components/common/Button";
import { Input } from "../components/common/Input";
import { Modal } from "../components/common/Modal";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { useDevices } from "../hooks/useDevices";
import { useDevicesStore } from "../store/devicesStore";
import { useAuthStore } from "../store/authStore";
import { hasPermission } from "../lib/permissions";
import { formatApiError } from "../lib/apiError";
import {
  createDevice, deleteDevice, rebootDevice, syncChannels, testConnection, updateDevice,
  bulkImportDevices, downloadDevicesXlsx, refreshChannelsBulk, testConnectionBulk,
  BulkImportResult, BulkRefreshChannelsResult, BulkTestConnectionResult,
} from "../api/devices";
import { BulkSyncTimeModal } from "./devices/BulkSyncTimeModal";
import { RevealCredentialsModal } from "./devices/RevealCredentialsModal";
import type { Device, VideoTransport } from "../types";
import { useDevicesTableStore, type SortKey } from "../store/devicesTableStore";

interface FormState {
  id?: number;
  name: string;
  host: string;
  http_port: number;
  rtsp_port: number;
  use_https: boolean;
  video_transport: VideoTransport;
  username: string;
  password: string;
  is_enabled: boolean;
  // String para permitir input vacio == "autodetectar todos". Se convierte a
  // number antes de enviar (0 == autodetectar, >0 == limite manual).
  channels_count: string;
  // Canales 1-based excluidos de la operacion.
  excluded_channels: number[];
}

const EMPTY_FORM: FormState = {
  name: "",
  host: "",
  http_port: 80,
  rtsp_port: 554,
  use_https: false,
  video_transport: "rtsp",
  username: "admin",
  password: "",
  is_enabled: true,
  channels_count: "",
  excluded_channels: [],
};

// Encabezado de columna clickeable para ordenar la tabla. Lee el criterio
// activo del store y dispara setSort al click.
function SortHeader({
  label,
  sortKey: colKey,
  align = "left",
}: {
  label: string;
  sortKey: SortKey;
  align?: "left" | "center";
}) {
  const sortKey = useDevicesTableStore((s) => s.sortKey);
  const sortDir = useDevicesTableStore((s) => s.sortDir);
  const setSort = useDevicesTableStore((s) => s.setSort);
  const active = sortKey === colKey;
  const Icon = !active ? ChevronsUpDown : sortDir === "asc" ? ChevronUp : ChevronDown;
  return (
    <th
      className={`px-3 py-2 cursor-pointer select-none hover:text-text ${
        align === "center" ? "text-center" : "text-left"
      }`}
      onClick={() => setSort(colKey)}
      title="Ordenar por esta columna"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon size={12} className={active ? "text-accent" : "opacity-40"} />
      </span>
    </th>
  );
}

export default function DevicesPage() {
  const me = useAuthStore((s) => s.user);
  const canManage = hasPermission(me, "can_manage_devices");
  const canConfig = hasPermission(me, "can_access_config");
  const { refetch } = useDevices();
  const devices = useDevicesStore((s) => s.devices);
  const nav = useNavigate();
  const queryClient = useQueryClient();
  // Invalida la lista global de canales (query "channels-all" de useDevices) para
  // que el arbol de "Vivo" se refresque tras sincronizar/detectar canales. El
  // refetch() de useDevices solo recarga la lista de dispositivos, no los canales.
  const refreshChannelList = () => queryClient.invalidateQueries({ queryKey: ["channels-all"] });

  const [openForm, setOpenForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<null | { kind: "delete" | "reboot"; deviceId: number; name: string }>(null);
  const [openImport, setOpenImport] = useState(false);
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [query, setQuery] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [openSyncTime, setOpenSyncTime] = useState(false);
  const [openRefreshConfirm, setOpenRefreshConfirm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState<BulkRefreshChannelsResult | null>(null);
  const [openTestAllConfirm, setOpenTestAllConfirm] = useState(false);
  const [testingAll, setTestingAll] = useState(false);
  const [testAllResult, setTestAllResult] = useState<BulkTestConnectionResult | null>(null);
  const [revealDevice, setRevealDevice] = useState<Device | null>(null);

  // Filtra por nombre o host (IP). Case-insensitive, sin acentos sensible.
  // Memoizado para no recalcular en cada render mientras no cambien devices/query.
  const filteredDevices = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) =>
      d.name.toLowerCase().includes(q) || d.host.toLowerCase().includes(q),
    );
  }, [devices, query]);

  const sortKey = useDevicesTableStore((s) => s.sortKey);
  const sortDir = useDevicesTableStore((s) => s.sortDir);

  // Ordena la lista YA filtrada por el buscador. Los vacios de model/firmware
  // van siempre al final (sin importar la direccion) y los empates se
  // desempatan por nombre, para un orden estable y prolijo.
  const sortedDevices = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const byName = (a: Device, b: Device) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
    const cmp = (a: Device, b: Device): number => {
      let primary = 0;
      if (sortKey === "name") {
        primary = byName(a, b);
      } else if (sortKey === "host") {
        primary = a.host.localeCompare(b.host, undefined, { numeric: true });
      } else if (sortKey === "channels") {
        primary = a.channels_count - b.channels_count;
      } else if (sortKey === "online") {
        primary = Number(a.is_online) - Number(b.is_online);
      } else {
        const va = a[sortKey];
        const vb = b[sortKey];
        if (!va && !vb) return byName(a, b);
        if (!va) return 1;
        if (!vb) return -1;
        primary = va.localeCompare(vb, undefined, { numeric: true });
      }
      return dir * primary || byName(a, b);
    };
    return [...filteredDevices].sort(cmp);
  }, [filteredDevices, sortKey, sortDir]);

  const allFilteredSelected = filteredDevices.length > 0 && filteredDevices.every((d) => selectedIds.has(d.id));
  const someFilteredSelected = filteredDevices.some((d) => selectedIds.has(d.id));
  const toggleSelectAllFiltered = () => {
    const next = new Set(selectedIds);
    if (allFilteredSelected) {
      filteredDevices.forEach((d) => next.delete(d.id));
    } else {
      filteredDevices.forEach((d) => next.add(d.id));
    }
    setSelectedIds(next);
  };
  const toggleSelectOne = (id: number) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  const doExport = async () => {
    try {
      await downloadDevicesXlsx();
      toast.success("Excel exportado");
    } catch (e) {
      toast.error(formatApiError(e, "Error exportando"));
    }
  };

  const onPickImportFile = async (f: File) => {
    if (!f) return;
    setImporting(true);
    setImportResult(null);
    const t = toast.loading(`Importando ${f.name}...`);
    try {
      const res = await bulkImportDevices(f);
      setImportResult(res);
      toast.success(
        `Listo: ${res.created} nuevos, ${res.updated} actualizados, ${res.skipped} omitidos`,
        { id: t },
      );
      await refetch();
    } catch (e) {
      toast.error(formatApiError(e, "Import fallo"), { id: t });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openNew = () => { setForm(EMPTY_FORM); setOpenForm(true); };
  const openEdit = (id: number) => {
    const d = devices.find((x) => x.id === id);
    if (!d) return;
    setForm({
      id: d.id, name: d.name, host: d.host, http_port: d.http_port,
      rtsp_port: d.rtsp_port, use_https: d.use_https,
      video_transport: d.video_transport ?? "rtsp", username: d.username,
      password: "", is_enabled: d.is_enabled,
      // Mostrar vacio si esta en 0 (autodetectar), sino el valor para edicion.
      channels_count: d.channels_count > 0 ? String(d.channels_count) : "",
      excluded_channels: d.excluded_channels ?? [],
    });
    setOpenForm(true);
  };

  // Convierte el campo string del form a number listo para la API. Vacio o
  // valor invalido -> undefined (omitido = mantener / autodetectar).
  const parseChannelsCount = (s: string): number | undefined => {
    const t = s.trim();
    if (!t) return undefined;
    const n = Number(t);
    if (!Number.isFinite(n) || n < 0) return undefined;
    return Math.floor(n);
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const channels_count = parseChannelsCount(form.channels_count);
      if (form.id) {
        const original = devices.find((d) => d.id === form.id);
        const patch: any = { ...form };
        if (!patch.password) delete patch.password;
        delete patch.id;
        // Reemplazar el string del form por el number listo para la API.
        // Si vacio, mandamos 0 para que el backend lo trate como autodetectar
        // y limpie un limite previo si existia.
        patch.channels_count = channels_count ?? 0;
        // Si cambio la cantidad de canales o las exclusiones, sincronizamos los
        // canales automaticamente tras guardar (ahorra el click manual).
        const sortNums = (a: number[]) => JSON.stringify([...a].sort((x, y) => x - y));
        const channelsChanged =
          !original ||
          (channels_count ?? 0) !== original.channels_count ||
          sortNums(form.excluded_channels) !== sortNums(original.excluded_channels ?? []);
        await updateDevice(form.id, patch);
        setOpenForm(false);
        if (channelsChanged) {
          const t = toast.loading("Sincronizando canales...");
          try {
            const res = await syncChannels(form.id);
            await refreshChannelList();
            toast.success(`Dispositivo actualizado. ${res.length} canales sincronizados.`, { id: t });
          } catch {
            toast.error(
              "Dispositivo actualizado, pero la sincronizacion automatica de canales fallo (el DVR no respondio).",
              { id: t },
            );
          }
        } else {
          toast.success("Dispositivo actualizado");
        }
      } else {
        if (!form.name.trim() || !form.host.trim() || !form.password) {
          toast.error("Nombre, host y password son obligatorios");
          setSubmitting(false);
          return;
        }
        const payload: any = { ...form };
        // En create, omitir channels_count si esta vacio (el backend lo deja
        // en 0 por defecto y _probe_and_sync lo autodetectara).
        if (channels_count === undefined) delete payload.channels_count;
        else payload.channels_count = channels_count;
        await createDevice(payload);
        toast.success("Dispositivo agregado. Probando conexion en background...");
        setOpenForm(false);
      }
      setForm((f) => ({ ...f, password: "" }));
      await refetch();
    } catch (e) {
      toast.error(formatApiError(e, "Error al guardar"));
    } finally {
      setSubmitting(false);
    }
  };

  const onTest = async (id: number) => {
    const t = toast.loading("Probando conexion...");
    try {
      const res = await testConnection(id);
      if (res.ok) toast.success(`OK: ${res.info?.model || ""}`, { id: t });
      else toast.error(`Fallo: ${res.message}`, { id: t });
      refetch();
    } catch (e) {
      toast.error(formatApiError(e), { id: t });
    }
  };

  const onSync = async (id: number) => {
    const t = toast.loading("Sincronizando canales...");
    try {
      const res = await syncChannels(id);
      await refreshChannelList();
      toast.success(`${res.length} canales sincronizados`, { id: t });
    } catch (e) {
      toast.error(formatApiError(e), { id: t });
    }
  };

  const onTestAllConfirmed = async () => {
    setOpenTestAllConfirm(false);
    if (testingAll) return;
    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
    const total = ids ? ids.length : devices.length;
    setTestingAll(true);
    const t = toast.loading(`Probando conexion en ${total} dispositivo${total === 1 ? "" : "s"}... esto puede tardar.`);
    try {
      const res = await testConnectionBulk(ids);
      const okN = res.online.length;
      const offN = res.offline.length;
      toast.success(`Listo: ${okN} online, ${offN} offline.`, { id: t });
      setTestAllResult(res);
      await refetch();
    } catch (e) {
      toast.error(formatApiError(e, "Error al probar conexion masiva"), { id: t });
    } finally {
      setTestingAll(false);
    }
  };

  const onRefreshChannelsConfirmed = async () => {
    setOpenRefreshConfirm(false);
    if (refreshing) return;
    const ids = selectedIds.size > 0 ? Array.from(selectedIds) : undefined;
    const total = ids ? ids.length : devices.length;
    setRefreshing(true);
    const t = toast.loading(`Detectando canales en ${total} dispositivo${total === 1 ? "" : "s"}... esto puede tardar.`);
    try {
      const res = await refreshChannelsBulk(ids);
      const changed = res.updated.length;
      const errs = res.errors.length;
      if (errs === 0) {
        toast.success(`Listo: ${changed} dispositivo${changed === 1 ? "" : "s"} actualizado${changed === 1 ? "" : "s"}.`, { id: t });
      } else {
        toast.success(`Listo con ${errs} error${errs === 1 ? "" : "es"}: ${changed} actualizado${changed === 1 ? "" : "s"}.`, { id: t });
      }
      setRefreshResult(res);
      await Promise.all([refetch(), refreshChannelList()]);
    } catch (e) {
      toast.error(formatApiError(e, "Error al detectar canales"), { id: t });
    } finally {
      setRefreshing(false);
    }
  };

  const onConfirmAction = async () => {
    if (!confirm) return;
    if (confirm.kind === "delete") {
      try { await deleteDevice(confirm.deviceId); toast.success("Eliminado"); refetch(); }
      catch (e) { toast.error(formatApiError(e)); }
    } else if (confirm.kind === "reboot") {
      try { await rebootDevice(confirm.deviceId); toast.success("Reiniciando..."); refetch(); }
      catch (e) { toast.error(formatApiError(e)); }
    }
    setConfirm(null);
  };

  // Total de canales para la grilla de exclusion del modal (solo edicion).
  // Prioriza lo tipeado en el form; cae al valor real del device.
  const editTotalChannels =
    parseChannelsCount(form.channels_count)
    ?? devices.find((d) => d.id === form.id)?.channels_count
    ?? 0;
  const toggleExcludedChannel = (n: number) => {
    const next = new Set(form.excluded_channels);
    if (next.has(n)) next.delete(n);
    else next.add(n);
    setForm({ ...form, excluded_channels: Array.from(next).sort((a, b) => a - b) });
  };

  return (
    <div className="p-4 overflow-auto h-full">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h1 className="text-lg font-semibold">Dispositivos</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre o IP..."
              className="bg-bg border border-line rounded pl-7 pr-7 py-1.5 text-sm text-text placeholder-muted focus:outline-none focus:border-accent w-56"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-text"
                title="Limpiar busqueda"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={doExport} title="Descargar Excel con todos los dispositivos (incluye contrasenas en texto plano)">
            <Download size={14} /> Exportar Excel
          </Button>
          {canManage && (
            <Button variant="ghost" size="sm" onClick={() => { setImportResult(null); setOpenImport(true); }} title="Importar dispositivos desde un archivo Excel">
              <Upload size={14} /> Importar Excel
            </Button>
          )}
          {canManage && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpenTestAllConfirm(true)}
              disabled={testingAll || devices.length === 0}
              title="Hace test-connection contra todos los NVR (o los seleccionados) y refresca modelo/firmware/online"
            >
              <TestTube size={14} /> {testingAll ? "Probando..." : "Probar todos"}
            </Button>
          )}
          {canManage && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpenRefreshConfirm(true)}
              disabled={refreshing || devices.length === 0}
              title="Consulta cada NVR y ajusta la cantidad de canales al numero real con camara conectada"
            >
              <Video size={14} /> {refreshing ? "Detectando..." : "Detectar canales"}
            </Button>
          )}
          {canManage && (
            <Button variant="primary" size="sm" onClick={openNew}>
              <Plus size={14} /> Agregar
            </Button>
          )}
        </div>
      </div>

      {canConfig && selectedIds.size > 0 && (
        <div className="mb-2 flex items-center justify-between gap-2 px-3 py-2 bg-accent/10 border border-accent rounded text-sm">
          <span>
            {selectedIds.size} dispositivo{selectedIds.size === 1 ? "" : "s"} seleccionado{selectedIds.size === 1 ? "" : "s"}
          </span>
          <div className="flex gap-2">
            <Button variant="primary" size="sm" onClick={() => setOpenSyncTime(true)}>
              <Clock size={14} /> Sincronizar hora y zona
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
              <X size={14} /> Limpiar selección
            </Button>
          </div>
        </div>
      )}

      <div className="border border-line rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-panel border-b border-line">
            <tr className="text-xs text-muted">
              {canConfig && (
                <th className="text-center px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    ref={(el) => { if (el) el.indeterminate = !allFilteredSelected && someFilteredSelected; }}
                    onChange={toggleSelectAllFiltered}
                    title="Seleccionar todos los visibles"
                  />
                </th>
              )}
              <SortHeader label="Nombre" sortKey="name" />
              <SortHeader label="Host" sortKey="host" />
              <SortHeader label="Modelo" sortKey="model" />
              <SortHeader label="FW" sortKey="firmware" />
              <SortHeader label="Canales" sortKey="channels" />
              <SortHeader label="Online" sortKey="online" align="center" />
              <th className="text-right px-3 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 && (
              <tr>
                <td colSpan={canConfig ? 8 : 7} className="text-center text-muted py-6">Sin dispositivos.</td>
              </tr>
            )}
            {devices.length > 0 && filteredDevices.length === 0 && (
              <tr>
                <td colSpan={canConfig ? 8 : 7} className="text-center text-muted py-6">
                  Ningun dispositivo coincide con "{query}".
                </td>
              </tr>
            )}
            {sortedDevices.map((d) => (
              <tr key={d.id} className="border-b border-line hover:bg-panel">
                {canConfig && (
                  <td className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(d.id)}
                      onChange={() => toggleSelectOne(d.id)}
                    />
                  </td>
                )}
                <td className="px-3 py-2 font-medium">{d.name}</td>
                <td className="px-3 py-2 text-muted">{d.host}:{d.http_port}</td>
                <td className="px-3 py-2 text-muted">{d.model || "-"}</td>
                <td className="px-3 py-2 text-muted">{d.firmware || "-"}</td>
                <td
                  className="px-3 py-2 text-muted"
                  title={d.excluded_channels.length > 0 ? `Canales excluidos: ${d.excluded_channels.join(", ")}` : undefined}
                >
                  {d.excluded_channels.length > 0
                    ? `${d.channels_count - d.excluded_channels.length} / ${d.channels_count}`
                    : d.channels_count}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-block w-2 h-2 rounded-full ${d.is_online ? "bg-green-500" : "bg-red-500"}`} />
                </td>
                <td className="px-3 py-2 text-right space-x-1">
                  {canManage && (
                    <Button variant="ghost" size="sm" onClick={() => onTest(d.id)} title="Probar conexion"><TestTube size={12} /></Button>
                  )}
                  {canManage && (
                    <Button variant="ghost" size="sm" onClick={() => onSync(d.id)} title="Sincronizar canales"><RefreshCw size={12} /></Button>
                  )}
                  {canConfig && (
                    <Button variant="ghost" size="sm" onClick={() => nav(`/devices/${d.id}/config`)} title="Configurar"><SettingsIcon size={12} /></Button>
                  )}
                  {canManage && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => setRevealDevice(d)} title="Ver datos (usuario, contraseña e IP)"><Eye size={12} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(d.id)} title="Editar">Editar</Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirm({ kind: "reboot", deviceId: d.id, name: d.name })} title="Reiniciar"><Power size={12} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirm({ kind: "delete", deviceId: d.id, name: d.name })} title="Eliminar"><Trash2 size={12} /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={openForm} onClose={() => setOpenForm(false)} title={form.id ? "Editar dispositivo" : "Agregar dispositivo"} width="lg">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Host / IP" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} />
          <Input label="Puerto HTTP" type="number" value={form.http_port} onChange={(e) => setForm({ ...form, http_port: Number(e.target.value) })} />
          <Input label="Puerto RTSP" type="number" value={form.rtsp_port} onChange={(e) => setForm({ ...form, rtsp_port: Number(e.target.value) })} />
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-muted">Transporte de video</span>
            <select
              className="bg-bg border border-line rounded px-2 py-1.5 text-sm text-text focus:outline-none focus:border-accent"
              value={form.video_transport}
              onChange={(e) => setForm({ ...form, video_transport: e.target.value as VideoTransport })}
            >
              <option value="rtsp">RTSP (estandar)</option>
              <option value="mjpeg">MJPEG por HTTP (NAT sin 554)</option>
              <option value="sdk">SDK NetSDK (sin RTSP)</option>
            </select>
          </label>
          <Input label="Usuario" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
          <Input
            label={form.id ? "Contrasena (vacio = no cambiar)" : "Contrasena"}
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <Input
            label="Cantidad de canales (vacio = autodetectar)"
            type="number"
            min={0}
            max={128}
            placeholder="ej: 8"
            value={form.channels_count}
            onChange={(e) => setForm({ ...form, channels_count: e.target.value })}
          />
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.use_https} onChange={(e) => setForm({ ...form, use_https: e.target.checked })} />
            HTTPS
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={form.is_enabled} onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })} />
            Habilitado
          </label>
        </div>

        {form.id && (
          <div className="mt-3 border-t border-line pt-3">
            {editTotalChannels > 0 ? (
              <>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-muted">
                    Excluir canales (click para sacarlos de la operacion)
                  </span>
                  <span className="text-xs text-muted">
                    Operativos:{" "}
                    {editTotalChannels -
                      form.excluded_channels.filter((n) => n >= 1 && n <= editTotalChannels).length}{" "}
                    de {editTotalChannels}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto">
                  {Array.from({ length: editTotalChannels }, (_, i) => i + 1).map((n) => {
                    const isExcluded = form.excluded_channels.includes(n);
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => toggleExcludedChannel(n)}
                        title={isExcluded ? `CH${n} excluido - click para incluir` : `CH${n} operativo - click para excluir`}
                        className={`w-12 h-8 rounded text-xs font-medium border transition-colors ${
                          isExcluded
                            ? "bg-red-500/15 border-red-500 text-red-400 line-through"
                            : "bg-bg border-line text-text hover:border-accent"
                        }`}
                      >
                        CH{n}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-xs text-muted">
                Defini la cantidad de canales para poder excluir canales individuales.
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setOpenForm(false)} disabled={submitting}>Cancelar</Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting ? "Guardando..." : "Guardar"}
          </Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirm !== null}
        title={confirm?.kind === "reboot" ? "Reiniciar DVR" : "Eliminar dispositivo"}
        message={
          confirm?.kind === "reboot"
            ? `Esta accion reiniciara el DVR "${confirm?.name}" Y CORTARA TODAS LAS GRABACIONES durante ~2 minutos. Continuar?`
            : `Eliminar "${confirm?.name}"? Se borraran tambien sus canales y se detendran sus streams.`
        }
        destructive
        confirmText={confirm?.kind === "reboot" ? "Reiniciar" : "Eliminar"}
        onConfirm={onConfirmAction}
        onCancel={() => setConfirm(null)}
      />

      <BulkSyncTimeModal
        open={openSyncTime}
        deviceIds={Array.from(selectedIds)}
        onClose={() => { setOpenSyncTime(false); refetch(); }}
      />

      <RevealCredentialsModal
        device={revealDevice}
        onClose={() => setRevealDevice(null)}
      />

      <ConfirmDialog
        open={openTestAllConfirm}
        title="Probar conexion en lote"
        message={
          selectedIds.size > 0
            ? `Se hara test-connection a ${selectedIds.size} dispositivo${selectedIds.size === 1 ? "" : "s"} seleccionado${selectedIds.size === 1 ? "" : "s"}. Refresca modelo, firmware y estado online. Continuar?`
            : `Se hara test-connection a TODOS los ${devices.length} dispositivos. Refresca modelo, firmware y estado online. Puede tardar varios minutos si hay muchos offline. Continuar?`
        }
        confirmText="Probar"
        onConfirm={onTestAllConfirmed}
        onCancel={() => setOpenTestAllConfirm(false)}
      />

      <Modal
        open={testAllResult !== null}
        onClose={() => setTestAllResult(null)}
        title="Resultado de Probar todos"
        width="lg"
      >
        {testAllResult && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3 text-center">
              <div>
                <div className="text-2xl font-semibold text-green-500">{testAllResult.online.length}</div>
                <div className="text-xs text-muted">Online</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-red-500">{testAllResult.offline.length}</div>
                <div className="text-xs text-muted">Offline</div>
              </div>
            </div>

            {testAllResult.online.length > 0 && (
              <div>
                <div className="font-semibold mb-1">Online ({testAllResult.online.length})</div>
                <div className="border border-line rounded max-h-64 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-panel border-b border-line sticky top-0">
                      <tr className="text-muted">
                        <th className="text-left px-2 py-1">Dispositivo</th>
                        <th className="text-left px-2 py-1">IP</th>
                        <th className="text-left px-2 py-1">Modelo</th>
                        <th className="text-left px-2 py-1">FW</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testAllResult.online.map((u) => (
                        <tr key={u.device_id} className="border-b border-line">
                          <td className="px-2 py-1 font-medium">{u.name}</td>
                          <td className="px-2 py-1 text-muted">{u.host}</td>
                          <td className="px-2 py-1 text-muted">{u.model || "-"}</td>
                          <td className="px-2 py-1 text-muted">{u.firmware || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {testAllResult.offline.length > 0 && (
              <div>
                <div className="font-semibold mb-1">Offline ({testAllResult.offline.length})</div>
                <div className="border border-line rounded max-h-64 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-panel border-b border-line sticky top-0">
                      <tr className="text-muted">
                        <th className="text-left px-2 py-1">Dispositivo</th>
                        <th className="text-left px-2 py-1">IP</th>
                        <th className="text-left px-2 py-1">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testAllResult.offline.map((u) => (
                        <tr key={u.device_id} className="border-b border-line">
                          <td className="px-2 py-1 font-medium">{u.name}</td>
                          <td className="px-2 py-1 text-muted">{u.host}</td>
                          <td className="px-2 py-1 text-red-400">{u.message || "DVR no responde"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end mt-4">
          <Button variant="primary" onClick={() => setTestAllResult(null)}>Cerrar</Button>
        </div>
      </Modal>

      <ConfirmDialog
        open={openRefreshConfirm}
        title="Detectar canales con camara"
        message={
          selectedIds.size > 0
            ? `Se consultaran ${selectedIds.size} NVR seleccionado${selectedIds.size === 1 ? "" : "s"} y se ajustara su cantidad de canales al numero real con camara conectada. Los layouts y descargas que referencien canales que ya no existen quedaran inaccesibles. Continuar?`
            : `Se consultaran TODOS los ${devices.length} NVR accesibles y se ajustara su cantidad de canales al numero real con camara conectada. Los layouts y descargas que referencien canales que ya no existen quedaran inaccesibles. Esto puede tardar varios minutos. Continuar?`
        }
        confirmText="Detectar"
        onConfirm={onRefreshChannelsConfirmed}
        onCancel={() => setOpenRefreshConfirm(false)}
      />

      <Modal
        open={refreshResult !== null}
        onClose={() => setRefreshResult(null)}
        title="Resultado de la deteccion de canales"
        width="lg"
      >
        {refreshResult && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-2xl font-semibold text-green-500">{refreshResult.updated.length}</div>
                <div className="text-xs text-muted">Actualizados</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-blue-500">{refreshResult.unchanged.length}</div>
                <div className="text-xs text-muted">Sin cambios</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-red-500">{refreshResult.errors.length}</div>
                <div className="text-xs text-muted">Errores</div>
              </div>
            </div>

            {refreshResult.updated.length > 0 && (
              <div>
                <div className="font-semibold mb-1">Actualizados ({refreshResult.updated.length})</div>
                <div className="border border-line rounded max-h-64 overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-panel border-b border-line sticky top-0">
                      <tr className="text-muted">
                        <th className="text-left px-2 py-1">Dispositivo</th>
                        <th className="text-center px-2 py-1">Antes</th>
                        <th className="text-center px-2 py-1">Despues</th>
                        <th className="text-left px-2 py-1">Eliminados</th>
                        <th className="text-left px-2 py-1">Agregados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {refreshResult.updated.map((u) => (
                        <tr key={u.device_id} className="border-b border-line">
                          <td className="px-2 py-1 font-medium">{u.name}</td>
                          <td className="px-2 py-1 text-center text-muted">{u.before}</td>
                          <td className="px-2 py-1 text-center text-green-500">{u.after}</td>
                          <td className="px-2 py-1 text-muted">
                            {u.removed_channels.length > 0 ? u.removed_channels.join(", ") : "-"}
                          </td>
                          <td className="px-2 py-1 text-muted">
                            {u.added_channels.length > 0 ? u.added_channels.join(", ") : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {refreshResult.unchanged.some((u) => u.skipped_reason) && (
              <div>
                <div className="font-semibold mb-1">
                  Sin cambios con motivo ({refreshResult.unchanged.filter((u) => u.skipped_reason).length})
                </div>
                <ul className="space-y-1 max-h-32 overflow-auto text-xs">
                  {refreshResult.unchanged.filter((u) => u.skipped_reason).map((u) => (
                    <li key={u.device_id} className="text-muted">
                      <span className="font-medium text-text">{u.name}</span>: {u.skipped_reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {refreshResult.errors.length > 0 && (
              <div>
                <div className="font-semibold mb-1">Errores ({refreshResult.errors.length})</div>
                <ul className="space-y-1 max-h-40 overflow-auto text-xs">
                  {refreshResult.errors.map((er, i) => (
                    <li key={i} className="text-red-400">
                      <span className="font-medium">{er.name ?? `device #${er.device_id}`}</span>: {er.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end mt-4">
          <Button variant="primary" onClick={() => setRefreshResult(null)}>Cerrar</Button>
        </div>
      </Modal>

      <Modal open={openImport} onClose={() => setOpenImport(false)} title="Importar dispositivos desde Excel" width="lg">
        <div className="space-y-3 text-sm">
          <p className="text-muted">
            Subi un archivo <b>.xlsx</b> con las columnas:{" "}
            <code className="text-xs">Nombre, Host (IP), Puerto HTTP, Puerto RTSP, HTTPS, Usuario, Contrasena, Cantidad canales</code>.
            Si no tenes la plantilla, descargala con <b>Exportar Excel</b> (sale con todos los dispositivos actuales,
            o con solo headers si no hay ninguno).
          </p>
          <p className="text-muted">
            Coincidencia por <b>host + puerto HTTP</b>: si ya existe se actualiza, si no se crea. La contrasena
            es obligatoria solo para nuevos; en updates dejala vacia para no cambiarla.
          </p>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onPickImportFile(f);
              }}
            />
            <Button
              variant="primary"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={importing}
            >
              <Upload size={14} /> {importing ? "Subiendo..." : "Elegir archivo Excel"}
            </Button>
          </div>

          {importResult && (
            <div className="border border-line rounded p-3 mt-2 bg-panel">
              <div className="grid grid-cols-3 gap-3 text-center mb-2">
                <div>
                  <div className="text-2xl font-semibold text-green-500">{importResult.created}</div>
                  <div className="text-xs text-muted">Creados</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-blue-500">{importResult.updated}</div>
                  <div className="text-xs text-muted">Actualizados</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold text-amber-500">{importResult.skipped}</div>
                  <div className="text-xs text-muted">Omitidos</div>
                </div>
              </div>
              {importResult.errors.length > 0 && (
                <div className="text-xs">
                  <div className="font-semibold mb-1">Errores ({importResult.errors.length}):</div>
                  <ul className="space-y-1 max-h-40 overflow-auto">
                    {importResult.errors.map((er, i) => (
                      <li key={i} className="text-red-400">
                        fila {er.row ?? "?"}{er.host ? ` (${er.host})` : ""}: {er.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="ghost" onClick={() => setOpenImport(false)}>Cerrar</Button>
        </div>
      </Modal>
    </div>
  );
}
